import { useState } from "react";
import {
  SearchedSubjectsList,
  SearchSubjectsParams,
  useSearchSubjectsMutation,
} from "../redux/apiSpecifications/apiSubjects";
import { useSelector } from "react-redux";
import { RootState } from "../redux/store";
import { FetchBaseQueryError } from "@reduxjs/toolkit/query";

type PagedSearchSubjectsParams = {
  pageNum: number;
  pageLen: number;
  scenario: "search";
  searchSubjectsParams: SearchSubjectsParams;
};

type SearchSubjectsState = {
  subjectsList?: SearchedSubjectsList;
  pagedSearchSubjectsParams: PagedSearchSubjectsParams;
};

const defaultState: SearchSubjectsState = {
  pagedSearchSubjectsParams: {
    pageLen: 5,
    pageNum: 1,
    scenario: "search",
    searchSubjectsParams: {},
  },
};

const useSearchSubjects = () => {
  const { activeRole } = useSelector((state: RootState) => state.user);

  const [state, setState] = useState<SearchSubjectsState>(defaultState);

  const [searchSubjects] = useSearchSubjectsMutation();

  const searchSubjectsPromise = (
    pagedSearchSubjectsParams: PagedSearchSubjectsParams
  ): Promise<SearchedSubjectsList> =>
    new Promise(async (resolve, reject) => {
      const response = await searchSubjects({
        ...pagedSearchSubjectsParams,
        activeRole: activeRole!,
      });

      const searchResponse = response as {
        data: SearchedSubjectsList;
      };

      if (searchResponse.data) {
        setState({
          ...state,
          pagedSearchSubjectsParams: {
            ...pagedSearchSubjectsParams,
          },
          subjectsList: searchResponse.data,
        });

        resolve(searchResponse.data);
      } else {
        const errorResponse = response as {
          error: FetchBaseQueryError;
        };

        reject(errorResponse.error);
      }
    });

  const changePaginationPromise = (
    pageIndex: number,
    pageSize: number
  ): Promise<SearchedSubjectsList> =>
    new Promise(async (resolve, reject) => {
      const response = await searchSubjects({
        ...state.pagedSearchSubjectsParams,
        activeRole: activeRole!,
        pageNum: pageIndex,
        pageLen: pageSize,
      });

      const searchResponse = response as {
        data: SearchedSubjectsList;
      };

      if (searchResponse.data) {
        setState({
          ...state,
          pagedSearchSubjectsParams: {
            ...state.pagedSearchSubjectsParams,
            pageNum: pageIndex,
            pageLen: pageSize,
          },
          subjectsList: searchResponse.data,
        });

        resolve(searchResponse.data);
      } else {
        const errorResponse = response as {
          error: FetchBaseQueryError;
        };

        reject(errorResponse.error);
      }
    });

  return {
    pagedSearchSubjectsParams: state?.pagedSearchSubjectsParams,
    subjectsList: state?.subjectsList,
    searchSubjectsPromise,
    changePaginationPromise,
  };
};

export default useSearchSubjects;
